import React, { useEffect, useRef, useState } from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import { getApiBaseUrl } from '../../../core/config/api';

interface ContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  /** code du type + nom (depuis la BDD) pour le libellé du bâtiment créé */
  onBuildClick: (buildingType: string, buildingName: string) => void;
}

export interface BuildingType {
  id: string;
  name: string;
  icon: string;
  color: string;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, onClose, onBuildClick }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const { t } = usePageTranslation('game', 'Map');
  const [buildingTypes, setBuildingTypes] = useState<BuildingType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    const apiUrl = getApiBaseUrl();
    fetch(`${apiUrl}/api/buildings/types`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data)) {
          setBuildingTypes(
            data.data.map((row: { code: string; nom: string; icon?: string; couleur?: string }) => ({
              id: row.code,
              name: row.nom,
              icon: row.icon || '🏢',
              color: row.couleur || 'text-gray-400',
            }))
          );
        }
      })
      .catch(() => setBuildingTypes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // Fermer le menu si on clique en dehors
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Fermer avec la touche Escape
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const menuHeight = buildingTypes.length > 0 ? buildingTypes.length * 48 + 20 : 80;
  const adjustedX = Math.min(x, window.innerWidth - 240);
  const adjustedY = Math.min(y, window.innerHeight - menuHeight);

  return (
    <div
      ref={menuRef}
      className="fixed z-[1000] bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-2 min-w-[220px] max-w-[280px]"
      style={{
        left: `${adjustedX}px`,
        top: `${adjustedY}px`,
      }}
    >
      <div className="px-3 py-2 border-b border-gray-700">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
          {t('contextMenu.buildBuilding') || 'Construire un bâtiment'}
        </p>
      </div>
      <div className="py-1">
        {loading ? (
          <p className="px-4 py-3 text-sm text-gray-500">{t('contextMenu.loading')}</p>
        ) : buildingTypes.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-500">{t('contextMenu.noTypes')}</p>
        ) : (
          buildingTypes.map((buildingType) => (
          <button
            key={buildingType.id}
            onClick={() => {
              onBuildClick(buildingType.id, buildingType.name);
              onClose();
            }}
            className="w-full px-4 py-2.5 text-left text-white hover:bg-gray-700 transition-colors flex items-center gap-3 text-sm group"
          >
            <span className="text-2xl">{buildingType.icon}</span>
            <span className="flex-1 font-medium">{buildingType.name}</span>
            <svg
              className="w-4 h-4 text-gray-500 group-hover:text-gray-300 transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
          ))
        )}
      </div>
    </div>
  );
};

export default ContextMenu;

